import React, { useState } from 'react';
import api, { isLimitError } from '../../api/api';
import { useCreateContext } from '../../product/CreateContext';
import { parseAiJson } from '../../utils/parseAiJson';
import { CopyButton, ErrorBanner, Field, GenerateButton, formGrid } from './generatorUi';
import { scriptAsText, useCreateFlow, usePrefill } from './flow';

const DURATIONS = [15, 30, 60];
const PLATFORMS = ['TikTok', 'Instagram Reels', 'YouTube Shorts'];

const ScriptScreen = () => {
    const { promptContext, seed } = useCreateContext();
    const { flow, report } = useCreateFlow();
    const [topic, setTopic] = useState(() => flow.topic || seed?.topic || '');
    usePrefill(flow.topic, topic, setTopic);
    // The hook picked in the Hook step; it follows later picks until you type your own
    const [hook, setHook] = useState(() => flow.hook || seed?.hook || '');
    usePrefill(flow.hook, hook, setHook);
    const [duration, setDuration] = useState(30);
    const [platform, setPlatform] = useState(() => PLATFORMS.find(p => seed?.platform && p.startsWith(seed.platform)) || PLATFORMS[0]);
    // The script is the flow's, so the Video and Review steps see it
    const { script } = flow;
    const setScript = (next) => report('script', { script: next }, Boolean(next));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleGenerate = async () => {
        if (!topic.trim() && !hook.trim()) {
            setError('Add a topic or a hook for the video.');
            return;
        }
        const prompt = `You are a short-form video strategist who writes scripts that hold attention to the end.
Write a ${duration}-second ${platform} video script.
${topic.trim() ? `Topic: ${topic.trim()}\n` : ''}${hook.trim() ? `Open with this hook (you may tighten it): ${hook.trim()}\n` : ''}
What you know about the product, who it's for and the brand voice (write for this person, in this voice):
${promptContext}

Break it into beats with timestamps that add up to about ${duration} seconds. Each beat has what's on screen, the voiceover, and optional on-screen text.
Show the product in use rather than describing it. End with one clear call to action.

Respond ONLY with JSON in this shape, no other text:
{"title": "working title", "hook": "the first line", "beats": [{"time": "0-3s", "visual": "...", "voiceover": "...", "onScreenText": "..."}], "cta": "..."}`;

        setError('');
        setIsLoading(true);
        try {
            const parsed = parseAiJson(await api.getChatCompletion(prompt));
            const beats = (parsed?.beats || []).filter(beat => beat && typeof beat === 'object').map(beat => ({
                time: String(beat.time || ''),
                visual: String(beat.visual || ''),
                voiceover: String(beat.voiceover || ''),
                onScreenText: String(beat.onScreenText || ''),
            }));
            if (beats.length === 0) throw new Error('No beats in response');
            setScript({
                title: String(parsed.title || topic || 'Video script'),
                hook: String(parsed.hook || hook),
                beats,
                cta: String(parsed.cta || ''),
            });
        } catch (e) {
            if (isLimitError(e)) return;
            setError("Couldn't write the script. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <ErrorBanner message={error} />
            <div className="ls-card" style={{ gap: 16 }}>
                <div className="ls-card-title">Script brief</div>
                <Field label="Topic">
                    <input className="ls-input" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Prep with me: night shift" />
                </Field>
                <Field label="Hook" hint="The hook you pick in the Hook step shows up here">
                    <input className="ls-input" value={hook} onChange={(e) => setHook(e.target.value)} placeholder="The first line viewers hear" />
                </Field>
                <div style={formGrid}>
                    <Field label="Length">
                        <select className="ls-select" value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
                            {DURATIONS.map(value => <option key={value} value={value}>{value} seconds</option>)}
                        </select>
                    </Field>
                    <Field label="Platform">
                        <select className="ls-select" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                            {PLATFORMS.map(value => <option key={value} value={value}>{value}</option>)}
                        </select>
                    </Field>
                </div>
                <GenerateButton isLoading={isLoading} onClick={handleGenerate} label={script ? 'Rewrite script' : 'Write script'} loadingLabel="Writing..." secondary={Boolean(script)} />
            </div>

            {script && (
                <div className="ls-card" style={{ gap: 14 }}>
                    <div className="ls-card-header">
                        <span className="ls-card-title">{script.title}</span>
                        <CopyButton text={scriptAsText(script)} />
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600 }}>“{script.hook}”</div>
                    {script.beats.map((beat, index) => (
                        <div
                            key={index}
                            style={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr)', gap: 12, borderTop: '1px solid var(--subtle)', paddingTop: 12, fontSize: 14, lineHeight: 1.5 }}
                        >
                            <span style={{ fontWeight: 600, color: 'var(--danger-text)' }}>{beat.time}</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <span style={{ color: 'var(--muted)' }}>{beat.visual}</span>
                                <span>{beat.voiceover}</span>
                                {beat.onScreenText && <span className="ls-chip" style={{ alignSelf: 'flex-start' }}>On screen: {beat.onScreenText}</span>}
                            </div>
                        </div>
                    ))}
                    {script.cta && (
                        <div style={{ borderTop: '1px solid var(--subtle)', paddingTop: 12, fontSize: 14 }}>
                            <b>CTA:</b> {script.cta}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ScriptScreen;
